import { Fn }			from "ts-base/fn";
import { Tuple2 }		from "ts-base/tuple";
import { Strings }		from "ts-base/strings";
import { Arrays }		from "ts-base/arrays";

// null means "do not add this parameter", array values are split into multiple parameters
export type UrlQuery	= Readonly<Record<string, UrlQuery.Param | ReadonlyArray<UrlQuery.Param>>>;

export namespace UrlQuery {
	export type Param	= string | number | boolean | null;

	// full-optional variant of the string function
	export const fullString	= (query:UrlQuery, questionMarkPrefix:"never"|"non-empty"|"always", sortOrder:"sorted"|"unsorted"):string	=> {
		const unsorted	= pairs(query);
		const sorted	=
			sortOrder === "sorted"		? sortPairs(unsorted)	:
			sortOrder === "unsorted"	? unsorted				:
			Fn.impossible(sortOrder);
		const qmark	=
			questionMarkPrefix === "never"		? false					:
			questionMarkPrefix === "always"		? true					:
			questionMarkPrefix === "non-empty"	? sorted.length !== 0	:
			Fn.impossible(questionMarkPrefix);
		return questionMark(qmark) + render(sorted);
	};

	// does not add a question mark at the front
	// identical to fullString(query, "never", "unsorted")
	export const string	= (query:UrlQuery):string	=>
		render(pairs(query));

	// result can be passed into new URLSearchParams
	export const pairs	= (query:UrlQuery):Array<Tuple2<string, string>>	=> {
		const out:Array<Tuple2<string, string>>	= [];
		for (const [ key, valueOrValues ] of Object.entries(query)) {
			const values	= Array.isArray(valueOrValues) ? [ ...valueOrValues ] : [ valueOrValues ];
			for (const value of values) {
				if (value === null)	continue;
				out.push([ key, value.toString() ]);
			}
		}
		return out;
	};

	// sorts pairs by key to stabilize the query string
	// NOTE this does _not_ change the order of multiple pairs with the same key
	export const sortPairs	= (pairs:ReadonlyArray<Tuple2<string, string>>):Array<Tuple2<string, string>>	=>
		Arrays.sortByWith(pairs)(([ k, _v ]) => k)(Strings.compare);

	export const questionMark	= (nonEmpty:boolean):string	=>
		nonEmpty ? "?" : "";

	// does not add a question mark at the front
	export const render	= (pairs:ReadonlyArray<Tuple2<string, string>>):string	=>
		joinPairs(pairs.map(renderPair));

	export const joinPairs	= (renderedPairs:ReadonlyArray<string>):string	=>
		renderedPairs.join("&");

	export const renderPair	= ([ k, v ]:Tuple2<string, string>):string	=>
		encodeURIComponent(k) + "=" + encodeURIComponent(v);
}
